import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateContactFileRequestsQueries } from './useContactFileRequestsQuery';

export const DEFAULT_REQUEST_FILE_LINK_EXPIRATION_DAYS = 30;

export interface ICreateFileRequestParams {
	/** The upload is attributed to this contact, so the link only works for them. */
	contactId: string;
	description?: string;
	/** ISO date-time after which the request stops accepting uploads. Without one, the account's link window applies. */
	expirationDate?: string;
	name: string;
}

/**
 * Creates a standalone file request for one contact (`POST sharedFile/request`) and resolves with the short link
 * they upload through. Without an expiration date, the account's default request-link window applies.
 */
export function useCreateFileRequestMutation() {
	const userSession = useUserSession();

	return useMutation({
		mutationFn: ({ contactId, description, expirationDate, name }: ICreateFileRequestParams) =>
			userSession.webServiceHelper.callAsync<string>(
				// No impersonation route exists for this endpoint, so no impersonation context is accepted.
				Api.ImpersonationBroker.composeApiUrl({
					queryParams: { contactId },
					urlPath: 'sharedFile/request',
				}),
				'POST',
				{
					_type: 'FileRequestSurvey',
					description: description?.trim() || undefined,
					expirationDate,
					name: name.trim(),
					// The client portal lists only requests flagged true; left unset, the contact never sees this one there.
					showInClientPortal: true,
				}
			),
		onSettled: (_, __, { contactId }) => invalidateContactFileRequestsQueries({ contactId }),
	});
}
